import React from "react";
import type { TFunction } from "i18next";
import type { Schema } from "@/shared/components/Form/types";
import { VideoFileField } from "@/shared/components/VideoFileField";

/** Цвет текста/кнопок: значения различаются, набор лейблов — общий. */
const COLOR_LABEL_KEYS: Record<"primary" | "secondary", string> = {
  primary: "platformCustomCovers.color.primary",
  secondary: "platformCustomCovers.color.secondary",
};

const TEXT_SIZE_LABEL_KEYS: Record<
  "header1" | "header2" | "header3" | "subtitle1" | "subtitle2" | "subtitle3",
  string
> = {
  header1: "platformCustomCovers.textSize.header1",
  header2: "platformCustomCovers.textSize.header2",
  header3: "platformCustomCovers.textSize.header3",
  subtitle1: "platformCustomCovers.textSize.subtitle1",
  subtitle2: "platformCustomCovers.textSize.subtitle2",
  subtitle3: "platformCustomCovers.textSize.subtitle3",
};

const BUTTON_SIZE_LABEL_KEYS: Record<"small" | "medium" | "large", string> = {
  small: "platformCustomCovers.buttonSize.small",
  medium: "platformCustomCovers.buttonSize.medium",
  large: "platformCustomCovers.buttonSize.large",
};

const BUTTON_VARIANT_LABEL_KEYS: Record<
  "contained" | "outlined" | "text",
  string
> = {
  contained: "platformCustomCovers.buttonVariant.contained",
  outlined: "platformCustomCovers.buttonVariant.outlined",
  text: "platformCustomCovers.buttonVariant.text",
};

const HORIZONTAL_ALIGN_LABEL_KEYS: Record<"left" | "center" | "right", string> =
  {
    left: "platformCustomCovers.hAlign.left",
    center: "platformCustomCovers.hAlign.center",
    right: "platformCustomCovers.hAlign.right",
  };

const VERTICAL_ALIGN_LABEL_KEYS: Record<"top" | "center" | "bottom", string> = {
  top: "platformCustomCovers.vAlign.top",
  center: "platformCustomCovers.vAlign.center",
  bottom: "platformCustomCovers.vAlign.bottom",
};

/** Цвет текста (textPrimary/textSecondary) — значения полей title/subTitle. */
const makeTextColorOptions = (t: TFunction) => [
  { value: "textPrimary", label: t(COLOR_LABEL_KEYS.primary) },
  { value: "textSecondary", label: t(COLOR_LABEL_KEYS.secondary) },
];

/** Цвет кнопок (primary/secondary) — значение поля buttonsColor. */
const makeButtonsColorOptions = (t: TFunction) => [
  { value: "primary", label: t(COLOR_LABEL_KEYS.primary) },
  { value: "secondary", label: t(COLOR_LABEL_KEYS.secondary) },
];

const makeTextSizeOptions = (t: TFunction) =>
  Object.entries(TEXT_SIZE_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

const makeButtonVariantOptions = (t: TFunction) =>
  Object.entries(BUTTON_VARIANT_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

const makeHorizontalAlignOptions = (t: TFunction) =>
  Object.entries(HORIZONTAL_ALIGN_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

const makeVerticalAlignOptions = (t: TFunction) =>
  Object.entries(VERTICAL_ALIGN_LABEL_KEYS).map(([value, key]) => ({
    value,
    label: t(key),
  }));

// Вес шрифта — латиница (Light/Regular/Medium/Bold), перевод не требуется.
const FONT_WEIGHT_OPTIONS = [
  { value: "300", label: "Light" },
  { value: "400", label: "Regular" },
  { value: "500", label: "Medium" },
  { value: "700", label: "Bold" },
];

// Схема для редактирования заголовка
export const makeCustomCoverTitleSchema = (t: TFunction): Schema => ({
  "title.text": {
    type: "text",
    label: t("platformCustomCovers.form.text"),
    xs: 12,
    fullWidth: true,
  },
  "title.color": {
    type: "select",
    label: t("platformCustomCovers.form.color"),
    xs: 12,
    fullWidth: true,
    options: makeTextColorOptions(t),
  },
  "title.size": {
    type: "select",
    label: t("platformCustomCovers.form.size"),
    xs: 12,
    fullWidth: true,
    options: makeTextSizeOptions(t),
  },
  "title.weight": {
    type: "select",
    label: t("platformCustomCovers.form.fontWeight"),
    xs: 12,
    fullWidth: true,
    options: FONT_WEIGHT_OPTIONS,
  },
});

// Схема для редактирования подзаголовка
export const makeCustomCoverSubTitleSchema = (t: TFunction): Schema => ({
  "subTitle.text": {
    type: "text",
    label: t("platformCustomCovers.form.text"),
    xs: 12,
    fullWidth: true,
  },
  "subTitle.color": {
    type: "select",
    label: t("platformCustomCovers.form.color"),
    xs: 12,
    fullWidth: true,
    options: makeTextColorOptions(t),
  },
  "subTitle.size": {
    type: "select",
    label: t("platformCustomCovers.form.size"),
    xs: 12,
    fullWidth: true,
    options: makeTextSizeOptions(t),
  },
  "subTitle.weight": {
    type: "select",
    label: t("platformCustomCovers.form.fontWeight"),
    xs: 12,
    fullWidth: true,
    options: FONT_WEIGHT_OPTIONS,
  },
});

// Схема для редактирования текста обложки (объединенная)
export const makeCustomCoverTextSchema = (t: TFunction): Schema => ({
  ...makeCustomCoverTitleSchema(t),
  ...makeCustomCoverSubTitleSchema(t),
});

// Схема для редактирования кнопок
export const makeCustomCoverButtonsSchema = (t: TFunction): Schema => ({
  buttonsSize: {
    type: "select",
    label: t("platformCustomCovers.form.size"),
    fullWidth: true,
    xs: 12,
    options: Object.entries(BUTTON_SIZE_LABEL_KEYS).map(([value, key]) => ({
      value,
      label: t(key),
    })),
  },
  buttonsColor: {
    type: "select",
    label: t("platformCustomCovers.form.color"),
    fullWidth: true,
    xs: 12,
    options: makeButtonsColorOptions(t),
  },
  button1Title: {
    type: "title",
    label: t("platformCustomCovers.form.button1Title"),
    fullWidth: true,
  },
  "buttons.0.disabled": {
    type: "switch",
  },
  "buttons.0.text": {
    type: "text",
    label: t("platformCustomCovers.form.text"),
    xs: 12,
    fullWidth: true,
  },
  "buttons.0.link": {
    type: "url",
    label: t("platformCustomCovers.form.link"),
    xs: 12,
    fullWidth: true,
  },
  "buttons.0.variant": {
    type: "select",
    label: t("platformCustomCovers.form.variant"),
    xs: 12,
    fullWidth: true,
    options: makeButtonVariantOptions(t),
  },
  button2Title: {
    type: "title",
    label: t("platformCustomCovers.form.button2Title"),
  },
  "buttons.1.disabled": {
    type: "switch",
  },
  "buttons.1.text": {
    type: "text",
    label: t("platformCustomCovers.form.text"),
    xs: 12,
    fullWidth: true,
  },
  "buttons.1.link": {
    type: "url",
    label: t("platformCustomCovers.form.link"),
    xs: 12,
    fullWidth: true,
  },
  "buttons.1.variant": {
    type: "select",
    label: t("platformCustomCovers.form.variant"),
    xs: 12,
    fullWidth: true,
    options: makeButtonVariantOptions(t),
  },
});

// Схема для редактирования фона
export const makeCustomCoverBackgroundSchema = (t: TFunction): Schema => ({
  "background.desktopImageId": {
    type: "image",
    label: t("platformCustomCovers.form.desktopImage"),
    imageType: "banner",
    xs: 12,
    fullWidth: true,
  },
  "background.mobileImageId": {
    type: "image",
    label: t("platformCustomCovers.form.mobileImage"),
    imageType: "banner",
    xs: 12,
    fullWidth: true,
  },
  // Видео идут сразу под картинками фона и в той же сетке: слот десктопа,
  // слот мобайла. Картинка остаётся фолбэком, пока видео не готово.
  "background.desktopVideoId": {
    type: "fieldComponent",
    label: t("videoFiles.field.desktop"),
    xs: 12,
    fullWidth: true,
    fieldComponent: ({ onChange, value, label }) =>
      React.createElement(VideoFileField, {
        onChange,
        value: (value as string | null) || null,
        label: label as string,
        helperText: t("videoFiles.hint"),
      }),
  },
  "background.mobileVideoId": {
    type: "fieldComponent",
    label: t("videoFiles.field.mobile"),
    xs: 12,
    fullWidth: true,
    fieldComponent: ({ onChange, value, label }) =>
      React.createElement(VideoFileField, {
        onChange,
        value: (value as string | null) || null,
        label: label as string,
        helperText: t("videoFiles.hint"),
      }),
  },
  brightness: {
    type: "number",
    label: t("platformCustomCovers.form.brightness"),
    xs: 12,
    fullWidth: true,
    helperText: t("platformCustomCovers.form.brightnessHelper"),
  },
});

// Схема для редактирования выравнивания
export const makeCustomCoverAlignSchema = (t: TFunction): Schema => ({
  horizontalAlignTitle: {
    type: "title",
    label: t("platformCustomCovers.form.horizontalAlignTitle"),
    variant: "subtitle1",
    xs: 12,
  },
  align: {
    // Desktop/Mobile — латиница, перевод не требуется.
    type: "select",
    label: "Desktop",
    xs: 12,
    fullWidth: true,
    options: makeHorizontalAlignOptions(t),
  },
  alignMobile: {
    type: "select",
    label: "Mobile",
    xs: 12,
    fullWidth: true,
    options: makeHorizontalAlignOptions(t),
  },
  verticalAlignTitle: {
    type: "title",
    label: t("platformCustomCovers.form.verticalAlignTitle"),
    variant: "subtitle1",

    xs: 12,
  },
  verticalAlign: {
    type: "select",
    label: "Desktop",
    xs: 12,
    fullWidth: true,
    options: makeVerticalAlignOptions(t),
  },
  verticalAlignMobile: {
    type: "select",
    label: "Mobile",
    xs: 12,
    fullWidth: true,
    options: makeVerticalAlignOptions(t),
  },
});

// Объединенная схема для редактирования обложки
export const makeCustomCoverEditSchema = (t: TFunction): Schema => ({
  ...makeCustomCoverTextSchema(t),
  ...makeCustomCoverButtonsSchema(t),
  ...makeCustomCoverBackgroundSchema(t),
  ...makeCustomCoverAlignSchema(t),
  publishAt: {
    type: "datetime",
    label: t("platformCustomCovers.form.publishAt"),
    xs: 12,
    fullWidth: true,
  },
});
